import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building2, User, Mail, Phone, MapPin, FileText, Lock, ArrowLeft, Upload, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const B2BRegisterPage = () => {
  const navigate = useNavigate();
  const { t, tError } = useLanguage();
  const [form, setForm] = useState({
    company_name: '', manager_name: '', email: '', phone: '', address: '', siret: '', password: '', password2: ''
  });
  const [kbisFile, setKbisFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const formatSiret = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 14);
    return digits.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.company_name || !form.manager_name || !form.email || !form.phone || !form.address || !form.siret || !form.password) {
      setError(t('b2bRegister.fillAll'));
      return;
    }
    if (form.siret.replace(/\s/g, '').length !== 14) {
      setError(t('b2bRegister.siretError'));
      return;
    }
    if (form.password !== form.password2) {
      setError(t('b2bRegister.pwdNoMatch'));
      return;
    }
    if (form.password.length < 6) {
      setError(t('b2bRegister.pwdMinLength'));
      return;
    }
    if (!kbisFile) {
      setError(t('b2bRegister.kbisRequired'));
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        company_name: form.company_name,
        manager_name: form.manager_name,
        email: form.email,
        phone: form.phone,
        address: form.address,
        siret: form.siret.replace(/\s/g, ''),
        password: form.password
      });

      const formData = new FormData();
      formData.append('kbis_file', kbisFile);

      await axios.post(`${API_URL}/api/b2b/register?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSuccess(true);
    } catch (err) {
      setError(tError(err.response?.data?.detail) || t('devisAccept.error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0a0a1a' }}>
        <div className="w-full max-w-md text-center space-y-6">
          <div className="w-20 h-20 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">{t('b2bRegister.success')}</h1>
          <p className="text-gray-400">{t('b2bRegister.successMsg')}</p>
          <button onClick={() => navigate('/login')} className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl font-semibold transition-colors" data-testid="b2b-back-login">
            {t('b2bRegister.goLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#0a0a1a' }}>
      <div className="absolute top-4 right-4 z-30"><LanguageSelector /></div>
      <div className="w-full max-w-2xl">
        <Link to="/login" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </Link>

        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">{t('b2bRegister.title')}</h1>
              <p className="text-sm text-gray-400">{t('b2bRegister.subtitle')}</p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm" data-testid="b2b-register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.companyName')} *</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="text" value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none transition-colors"
                    placeholder="Ex: Tech Guyane SARL" data-testid="b2b-company-name" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.managerName')} *</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="text" value={form.manager_name} onChange={e => setForm({...form, manager_name: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none transition-colors"
                    placeholder="Nom complet" data-testid="b2b-manager-name" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.proEmail')} *</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none transition-colors"
                    placeholder="contact@entreprise.com" data-testid="b2b-email" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.phone')} *</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none transition-colors"
                    placeholder="0694..." data-testid="b2b-phone" />
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.address')} *</label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="text" value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none transition-colors"
                    placeholder="Adresse complète" data-testid="b2b-address" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.siret')} *</label>
                <div className="relative">
                  <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="text" value={form.siret} onChange={e => setForm({...form, siret: formatSiret(e.target.value)})}
                    className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none transition-colors"
                    placeholder="123 456 789 00012" data-testid="b2b-siret" maxLength={18} />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.kbis')} *</label>
                <label className="flex items-center gap-2 w-full py-2.5 px-3 bg-white/5 border border-white/10 rounded-xl text-sm cursor-pointer hover:border-purple-500/50 transition-colors">
                  <Upload className="w-4 h-4 text-gray-500" />
                  <span className={kbisFile ? 'text-purple-400' : 'text-gray-500'}>
                    {kbisFile ? kbisFile.name : t('b2bRegister.dropFile')}
                  </span>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={e => setKbisFile(e.target.files[0])}
                    className="hidden" data-testid="b2b-kbis-file" />
                </label>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.password')} *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none transition-colors"
                    placeholder="Min. 6 caractères" data-testid="b2b-password" />
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1">{t('b2bRegister.confirmPassword')} *</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <input type="password" value={form.password2} onChange={e => setForm({...form, password2: e.target.value})}
                    className="w-full pl-10 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-xl text-white text-sm focus:border-purple-500/50 focus:outline-none transition-colors"
                    placeholder="Confirmer" data-testid="b2b-password2" />
                </div>
              </div>
            </div>

            <button type="submit" disabled={loading}
              className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50 mt-2"
              data-testid="b2b-register-submit">
              {loading ? t('b2bRegister.creating') : t('b2bRegister.submit')}
            </button>
          </form>

          <p className="text-center text-gray-500 text-sm mt-4">
            {t('b2bRegister.alreadyAccount')} <Link to="/login" className="text-purple-400 hover:text-purple-300">{t('login.connect')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default B2BRegisterPage;
